<script setup lang="ts">
import {reactive, ref} from "vue";
import {ElNotification, UploadUserFile} from "element-plus";
import {CirclePlus, Remove} from "@element-plus/icons-vue";
import {blobToFile, durationList, GroupDiscountUtil} from "@/utils/common.ts";
import SingleImageUploader from "components/common/SingleImageUploader.vue";
import {AddCourse, CourseCate, CourseSubCate, GroupCourseDiscount} from "@/types";
import MultiImageUploader from "components/common/MultiImageUploader.vue";
import html2canvas from "html2canvas";
import {ProgressInfo} from "cos-js-sdk-v5";
import {uploadImage} from "@/utils/cos.ts";
import {SystemService} from "@/api/api.ts";

const cateList = ref<CourseCate[]>([])
const twoCateList = ref<CourseSubCate[]>()
const imgContainer = ref<HTMLDivElement>();

const props = defineProps<{
  // 课程类型
  type: number
}>()

const form = reactive({
  // 标题
  title: "",
  // 简介
  short_detail: "",
  // 简介图
  content: "",
  // 每讲时长（分钟），直播课时必传
  duration: 45,
  // 组合课优惠描述，组合课必传
  sign_time: "",
  // 课程价格（组合课时可不传）
  price: "",
  // 优惠信息；如：[{“count”:1,”discount”:0.9},{“count”:3,”discount”:0.8}]
  describeInfo: [{count: 2, discount: 9.5}] as GroupCourseDiscount[],
  // 直播课时是否为伪直播，1=是，0=否
  is_fake: 1,
  // 分类标签id
  cate_id: undefined,
  // 二级分类id
  two_cate_id: undefined,
  // 是否为私密课，1=是，0=否
  is_secret: 0,
  fileList: [] as UploadUserFile[],
})

const thumbImageUploader = ref<InstanceType<typeof SingleImageUploader>>()
const multiImageUploader = ref<InstanceType<typeof MultiImageUploader>>()

const emit = defineEmits(["onAddCourse"])

const initCate = async () => {
  try {
    const res = await SystemService.getCourseCateList({})
    if (res.data.code == 200) {
      cateList.value = res.data.data || [];
    }
  } catch (err) {
    console.warn(err)
  }
}

initCate()

const onCateChange = (cateId: any) => {
  for (let i = 0; i < cateList.value.length; i++) {
    if (cateList.value[i].id === cateId) {
      form.two_cate_id = undefined
      twoCateList.value = cateList.value[i].two_list || []
      break
    }
  }
}

const onSubmit = () => {
  const info = genData()
  if (info) {
    if (info.title.length === 0) {
      return ElNotification.warning({
        message: "请填写课程标题"
      });
    }
    if ((!info.content || info.content.length === 0) && form.short_detail.length === 0) {
      return ElNotification.warning({
        message: "请上传课程简介图或填写课程简介"
      })
    }
    if (info.price.length === 0) {
      return ElNotification.warning({
        message: "请填写课程价格"
      })
    }
    emit("onAddCourse", info)
  }
}

// 添加新的折扣
const onAddDiscountClick = () => {
  form.describeInfo.push({
    count: 2,
    discount: 9.5,
  })
}

const onRemoveDiscountClick = () => {
  form.describeInfo.splice(form.describeInfo.length - 1, 1)
}

// 表单数据生成
const genData = (): AddCourse | undefined => {
  let info: AddCourse = Object.assign({})
  if (props.type === 1) {
    if (genLiveCourseData(info)) {
      return info
    }
  } else if (props.type === 2) {
    if (genRecordCourseData(info)) {
      return info
    }
  } else if (props.type === 3) {
    if (genSeriesCourseData(info)) {
      return info
    }
  } else if (props.type === 4) {
    if (genGroupCourseData(info)) {
      return info
    }
  }
  return undefined;
}

// 直播课表单数据生成
const genLiveCourseData = (info: AddCourse): boolean => {
  info.type = 1
  info.title = form.title
  info.cate_id = form.cate_id
  info.two_cate_id = form.two_cate_id
  info.is_secret = form.is_secret
  info.sign_time = form.sign_time
  info.duration = form.duration
  info.is_fake = form.is_fake
  info.short_detail = form.short_detail
  const images = []
  if (multiImageUploader.value?.fileList) {
    const list = multiImageUploader.value?.fileList
    for (let i = 0; i < list.length; i++) {
      if (list[i].status === 'success') {
        images.push(list[i].url)
      } else if (list[i].status === 'uploading') {
        ElNotification.warning({
          title: "提醒",
          message: "当前简介图片正在上传中, 请稍后"
        })
        return false
      } else if (list[i].status === 'fail') {
        ElNotification.warning({
          title: "提醒",
          message: "部分简介图片上传失败，请删除或重新上传"
        })
        return false
      }
    }
  }
  info.content = images.join(",")
  let thumb = ""
  if (thumbImageUploader.value?.fileList) {
    const list = thumbImageUploader.value?.fileList
    if (list.length > 0) {
      if (list[0].status === 'success') {
        thumb = list[0].url || ''
      } else if (list[0].status === 'uploading') {
        ElNotification.warning({
          title: "提醒",
          message: "当前封面图片正在上传中, 请稍后"
        })
        return false
      } else if (list[0].status === 'fail') {
        ElNotification.warning({
          title: "提醒",
          message: "当前封面图片上传失败，请删除或重新上传"
        })
        return false
      }
    }
  }
  info.thumb_image = thumb
  info.price = form.price
  return true
}

// 录播课表单数据生成
const genRecordCourseData = (info: AddCourse): boolean => {
  info.type = 2
  info.title = form.title
  info.cate_id = form.cate_id
  info.two_cate_id = form.two_cate_id
  info.is_secret = form.is_secret
  info.short_detail = form.short_detail
  const images = []
  if (multiImageUploader.value?.fileList) {
    const list = multiImageUploader.value?.fileList
    for (let i = 0; i < list.length; i++) {
      if (list[i].status === 'success') {
        images.push(list[i].url)
      } else if (list[i].status === 'uploading') {
        ElNotification.warning({
          title: "提醒",
          message: "当前简介图片正在上传中, 请稍后"
        })
        return false
      } else if (list[i].status === 'fail') {
        ElNotification.warning({
          title: "提醒",
          message: "部分简介图片上传失败，请删除或重新上传"
        })
        return false
      }
    }
  }
  info.content = images.join(",")
  let thumb = ""
  if (thumbImageUploader.value?.fileList) {
    const list = thumbImageUploader.value?.fileList
    if (list.length > 0) {
      if (list[0].status === 'success') {
        thumb = list[0].url || ''
      } else if (list[0].status === 'uploading') {
        ElNotification.warning({
          title: "提醒",
          message: "当前封面图片正在上传中, 请稍后"
        })
        return false
      } else if (list[0].status === 'fail') {
        ElNotification.warning({
          title: "提醒",
          message: "当前封面图片上传失败，请删除或重新上传"
        })
        return false
      }
    }
  }
  info.thumb_image = thumb
  info.price = form.price
  return true
}

// 系列课表单数据生成
const genSeriesCourseData = (info: AddCourse): boolean => {
  info.type = 3
  info.title = form.title
  info.cate_id = form.cate_id
  info.two_cate_id = form.two_cate_id
  info.is_secret = form.is_secret
  info.short_detail = form.short_detail
  const images = []
  if (multiImageUploader.value?.fileList) {
    const list = multiImageUploader.value?.fileList
    for (let i = 0; i < list.length; i++) {
      if (list[i].status === 'success') {
        images.push(list[i].url)
      } else if (list[i].status === 'uploading') {
        ElNotification.warning({
          title: "提醒",
          message: "当前简介图片正在上传中, 请稍后"
        })
        return false
      } else if (list[i].status === 'fail') {
        ElNotification.warning({
          title: "提醒",
          message: "部分简介图片上传失败，请删除或重新上传"
        })
        return false
      }
    }
  }
  info.content = images.join(",")
  let thumb = ""
  if (thumbImageUploader.value?.fileList) {
    const list = thumbImageUploader.value?.fileList
    if (list.length > 0) {
      if (list[0].status === 'success') {
        thumb = list[0].url || ''
      } else if (list[0].status === 'uploading') {
        ElNotification.warning({
          title: "提醒",
          message: "当前封面图片正在上传中, 请稍后"
        })
        return false
      } else if (list[0].status === 'fail') {
        ElNotification.warning({
          title: "提醒",
          message: "当前封面图片上传失败，请删除或重新上传"
        })
        return false
      }
    }
  }
  info.thumb_image = thumb
  info.price = form.price
  return true
}

// 组合课表单数据生成
const genGroupCourseData = (info: AddCourse): boolean => {
  info.type = 4
  info.title = form.title
  info.cate_id = form.cate_id
  info.two_cate_id = form.two_cate_id
  info.is_secret = form.is_secret
  info.short_detail = form.short_detail
  const images = []
  if (multiImageUploader.value?.fileList) {
    const list = multiImageUploader.value?.fileList
    for (let i = 0; i < list.length; i++) {
      if (list[i].status === 'success') {
        images.push(list[i].url)
      } else if (list[i].status === 'uploading') {
        ElNotification.warning({
          title: "提醒",
          message: "当前简介图片正在上传中, 请稍后"
        })
        return false
      } else if (list[i].status === 'fail') {
        ElNotification.warning({
          title: "提醒",
          message: "部分简介图片上传失败，请删除或重新上传"
        })
        return false
      }
    }
  }
  info.content = images.join(",")
  let thumb = ""
  if (thumbImageUploader.value?.fileList) {
    const list = thumbImageUploader.value?.fileList
    if (list.length > 0) {
      if (list[0].status === 'success') {
        thumb = list[0].url || ''
      } else if (list[0].status === 'uploading') {
        ElNotification.warning({
          title: "提醒",
          message: "当前封面图片正在上传中, 请稍后"
        })
        return false
      } else if (list[0].status === 'fail') {
        ElNotification.warning({
          title: "提醒",
          message: "当前封面图片上传失败，请删除或重新上传"
        })
        return false
      }
    }
  }
  info.thumb_image = thumb
  info.sign_time = genDescribe()
  const myDescribeInfo = form.describeInfo
  myDescribeInfo.forEach(item => {
    item.discount = Number(item.discount / 10)
  })
  info.describe_info = JSON.stringify(myDescribeInfo)
  info.price = "0"
  return true
}

// 组合课描述生成
const genDescribe = (): string => {
  const describe = []
  for (let i = 0; i < form.describeInfo.length; i++) {
    describe.push(`${form.describeInfo[i].count}门${form.describeInfo[i].discount}折`)
  }
  return describe.join(",")
}

const genThumb = async() => {
  const canvas = await html2canvas(imgContainer.value!!);
  canvas.toBlob(
      async blob => {
        const newFile = blobToFile(blob!!, "thumb.png");
        uploadImage(newFile, {
          onTaskReady: (taskId: string) => {
            console.log(taskId)
          },
          onProgress: (progressData: ProgressInfo) => {
            console.log(progressData.percent)
          },
          onUploadFail: (err: string) => {
            console.log(err)
          },
          onFileFinish: (url: string) => {
            console.log(url)
            thumbImageUploader.value?.init(url)
          }
        })
      },
      "image/png",
      1
  );
}
</script>

<template>
  <el-form :model="form" label-width="auto">
    <el-form-item label="课程标题">
      <el-input v-model="form.title" placeholder="请输入课程标题" maxlength="30" show-word-limit/>
    </el-form-item>
    <el-form-item label="课程分类">
      <el-select v-model="form.cate_id" placeholder="选择主分类(必选)" @change="onCateChange">
        <el-option v-for="item in cateList" :label="item.name" :value="item.id"></el-option>
      </el-select>
    </el-form-item>
    <el-form-item label="二级分类">
      <el-select v-model="form.two_cate_id" placeholder="选择子分类(可选)">
        <el-option v-for="item in twoCateList" :label="item.name" :value="item.id"></el-option>
      </el-select>
    </el-form-item>
    <el-form-item label="课程封面">
      <single-image-uploader ref="thumbImageUploader" @on-generate="genThumb"></single-image-uploader>
    </el-form-item>
    <el-form-item label="课程简介">
      <el-input v-model="form.short_detail" type="textarea"
                placeholder="请输入课程简介(文字描述与图片至少填一项)"
      :autosize="{
        minRows: 3,
        maxRows: 6
      }"></el-input>
    </el-form-item>
    <el-form-item label=" ">
      <multi-image-uploader :max="3" ref="multiImageUploader"/>
    </el-form-item>
    <el-form-item label="每讲时长" v-if="type === 1">
      <div class="flex">
        <el-select v-model="form.duration" style="width: 10rem">
          <el-option v-for="item in durationList()" :label="item" :value="item">
          </el-option>
        </el-select>
        <div class="ml-1rem">分钟</div>
      </div>
    </el-form-item>
    <el-form-item label="上课时间" v-if="type === 1">
      <el-input v-model="form.sign_time" placeholder="请输入上课时间, 如：每周六晚九点上课、详情见课表安排等"/>
    </el-form-item>
    <el-form-item label="直播类型" v-if="type === 1">
      <el-radio-group v-model="form.is_fake" disabled>
        <el-radio :value="0" label="直播"></el-radio>
        <el-radio :value="1" label="AI直播"></el-radio>
      </el-radio-group>
    </el-form-item>
    <el-form-item label="课程定位">
      <el-select v-model="form.is_secret" placeholder="请选择课程定位">
        <el-option label="公开" :value="0"></el-option>
        <el-option label="私密" :value="1"></el-option>
        <el-option label="联培" :value="2"></el-option>
      </el-select>
    </el-form-item>
    <!-- 组合课 -->
    <el-form-item label="优惠信息" v-if="type === 4">
      <template v-for="(item, index) in form.describeInfo">
        <div class="flex gap-1.25rem">
          <el-select v-model="item.count" style="width: 10rem">
            <el-option v-for="item in GroupDiscountUtil.countList" :value="item" :label="item"></el-option>
          </el-select>
          门
          <el-select v-model="item.discount" style="width: 10rem">
            <el-option v-for="item in GroupDiscountUtil.discountList" :value="item" :label="item"></el-option>
          </el-select>
          折 {{ index !== form.describeInfo.length - 1 ? ', ' : '' }}
        </div>
      </template>
      <div class="flex items-center ml-1rem cursor-pointer text-red-3" @click="onRemoveDiscountClick"
           v-if=" form.describeInfo.length > 1">
        <el-icon>
          <Remove/>
        </el-icon>
        <span class="ml-.25rem">
              移除折扣
          </span>
      </div>
      <div class="flex items-center ml-1rem cursor-pointer text-blue" @click="onAddDiscountClick"
           v-if=" form.describeInfo.length < 4">
        <el-icon>
          <CirclePlus/>
        </el-icon>
        <span class="ml-.25rem">
              添加折扣
          </span>
      </div>
    </el-form-item>
    <el-form-item label="价格" v-if="type !== 4">
      <div class="flex">
        <el-input type="number" v-model="form.price" placeholder="请输入课程价格（填0即免费）" style="width: 20rem"/>
        <div class="ml-1rem">元</div>
      </div>
    </el-form-item>
    <div class="flex items-end justify-end">
      <el-button type="primary" @click="onSubmit">确认创建</el-button>
    </div>
  </el-form>
  <div class="img-preview-large" ref="imgContainer">
    {{ form.title }}
  </div>
</template>

<style scoped>
.img-preview-large {
  background-image: url("@/assets/image/thumb_bg.png");
  background-repeat: no-repeat;
  background-size: 750px 422px;
  -moz-background-size: 100% 100%;
  width: 750px;
  height: calc(750px * 9 / 16);
  color: white;
  font-weight: bold;
  font-size: 50px;
  padding: 96px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  line-height: 1.3;
  position: absolute;
  top: -9999px;
  left: -9999px;
}
</style>
